import { useMemo } from 'react'
import { Box, Paper } from '@mui/material'
import { useMeasurementUnits } from '~/common/use-measurement-units'

/**
 * Calculate polygon area using Shoelace formula
 * @param {Array<{x: number, y: number}>} points - Polygon vertices
 * @returns {number} - Area in square pixels
 */
export const calculatePolygonArea = (points) => {
  if (!points || points.length < 3) return 0

  let area = 0
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length
    area += points[i].x * points[j].y
    area -= points[j].x * points[i].y
  }
  return Math.abs(area / 2)
}

/**
 * Convert pixel area to square meters based on geographic bounds
 * @param {number} pixelArea - Area in square pixels (0-1000 coordinate space)
 * @param {Array} bounds - Geographic bounds array [minLng, minLat, maxLng, maxLat]
 * @returns {number} - Area in square meters
 */
export const pixelAreaToSquareMeters = (pixelArea, bounds) => {
  if (!bounds || bounds.length !== 4) return 0

  // Bounds array format: [minLng, minLat, maxLng, maxLat]
  const [minLng, minLat, maxLng, maxLat] = bounds

  // Calculate meters per pixel in both dimensions
  const latRange = maxLat - minLat
  const lngRange = maxLng - minLng

  // Approximate meters per degree at the given latitude
  const avgLat = (minLat + maxLat) / 2
  const metersPerDegreeLat = 111320 // Roughly constant
  const metersPerDegreeLng = 111320 * Math.cos((avgLat * Math.PI) / 180)

  // Calculate total area in meters
  const heightMeters = latRange * metersPerDegreeLat
  const widthMeters = lngRange * metersPerDegreeLng

  // Meters per pixel
  const metersPerPixelX = widthMeters / 1000
  const metersPerPixelY = heightMeters / 1000

  // Convert pixel area to square meters
  return pixelArea * metersPerPixelX * metersPerPixelY
}

/**
 * CSS-based overlay showing completed polygon area
 * Positioned at polygon centroid using screen coordinates
 */
export const AreaMeasurement = ({ points, isComplete, climateBounds, centroidScreenPos }) => {
  const { formatArea } = useMeasurementUnits()

  // Calculate area in square meters
  const areaM2 = useMemo(() => {
    if (!isComplete || !points || points.length < 3 || !climateBounds) {
      return 0
    }

    const pixelArea = calculatePolygonArea(points)
    return Math.round(pixelAreaToSquareMeters(pixelArea, climateBounds))
  }, [points, isComplete, climateBounds])

  if (!isComplete || areaM2 === 0 || !centroidScreenPos) {
    return null
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        left: centroidScreenPos.x,
        top: centroidScreenPos.y,
        transform: 'translate(-50%, -50%)',
        px: 2,
        pb: '0.25rem',
        borderRadius: 1.5,
        pointerEvents: 'none',
        zIndex: 1000,
        bgcolor: 'background.paper',
        border: '2px solid',
        borderColor: 'primary.main',
      }}
    >
      <Box
        component="span"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          fontWeight: 600,
          color: 'primary.main',
        }}
      >
        {formatArea(areaM2)}
      </Box>
    </Paper>
  )
}
